import { Link } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useTours } from '../lib/tours'
import { useCollection } from '../lib/firestore'
import { countdown, formatDateRange, formatDay, formatTime, isOnDay } from '../lib/format'
import type { ItineraryItem, Show, Tour, WithId } from '../types/models'
import Page from '../components/Page'
import Poster from '../components/Poster'
import Empty from '../components/Empty'

export default function Dashboard() {
  const { uid } = useAuth()
  const { tours, loading } = useTours(uid ?? undefined)

  const now = new Date()
  const current =
    tours.find(
      (tour) => tour.startDate.toDate() <= now && tour.endDate.toDate() >= now,
    ) ??
    // Nothing running: the nearest tour either side of today is still the one
    // the user came here to look at.
    [...tours].sort(
      (a, b) =>
        Math.abs(a.startDate.toMillis() - now.getTime()) -
        Math.abs(b.startDate.toMillis() - now.getTime()),
    )[0]

  if (loading) return <Page title="Dashboard" />

  if (!current) {
    return (
      <Page title="Dashboard">
        <Empty
          title="No tours yet"
          message="Create your first tour and the schedule, crew and travel all hang off it."
          action={{ label: 'Create a tour', to: '/tours' }}
        />
      </Page>
    )
  }

  return (
    <Page title="Dashboard">
      <TourHero tour={current} />
      <TourBody tour={current} />
    </Page>
  )
}

/* -------------------------------------------------------------------- hero */

function TourHero({ tour }: { tour: WithId<Tour> }) {
  const isRunning =
    tour.startDate.toDate() <= new Date() && tour.endDate.toDate() >= new Date()

  return (
    <Link to={`/tours/${tour.id}`} className="panel" style={{ overflow: 'hidden', display: 'block' }}>
      <div style={{ display: 'flex', gap: 20, padding: 20 }}>
        <Poster url={tour.posterURL} width={104} />

        <div className="stack" style={{ gap: 8, minWidth: 0, justifyContent: 'center' }}>
          <span className="row" style={{ gap: 7 }}>
            <span className="dot dot-accent" />
            <span className="t-eyebrow accent">{isRunning ? 'On tour' : 'Next tour'}</span>
          </span>

          <h2 className="t-display-l truncate">{tour.tourName}</h2>

          <div className="row t-spec-caption dim" style={{ gap: 10, flexWrap: 'wrap' }}>
            <span>{tour.artist}</span>
            <span aria-hidden>·</span>
            <span>{formatDateRange(tour.startDate, tour.endDate)}</span>
          </div>
        </div>
      </div>
    </Link>
  )
}

/* ------------------------------------------------------------------- body */

function TourBody({ tour }: { tour: WithId<Tour> }) {
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const items = useCollection<ItineraryItem>('itineraryItems', [
    where('tourId', '==', tour.id),
  ])

  const now = new Date()
  const sortedShows = [...shows.data].sort(
    (a, b) => a.date.toMillis() - b.date.toMillis(),
  )
  const nextShow = sortedShows.find((show) => show.date.toDate() >= startOfToday(now))

  const today = [...items.data]
    .filter((item) => isOnDay(item.timeUTC, now, item.timezone))
    .sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())

  const upcoming = [...items.data]
    .filter((item) => item.timeUTC.toDate() >= now)
    .sort((a, b) => a.timeUTC.toMillis() - b.timeUTC.toMillis())

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 20 }}>
      <div className="sections">
        <section className="section">
          <h3 className="t-eyebrow">Today</h3>
          <div className="panel rows">
            {today.length === 0 ? (
              <p className="panel-pad t-caption">Nothing scheduled today.</p>
            ) : (
              today.map((item) => <ItemRow key={item.id} item={item} />)
            )}
          </div>
        </section>

        <section className="section">
          <h3 className="t-eyebrow">Next up</h3>
          <div className="panel rows">
            {upcoming.length === 0 ? (
              <p className="panel-pad t-caption">Nothing scheduled.</p>
            ) : (
              upcoming.slice(0, 6).map((item) => <ItemRow key={item.id} item={item} showDate />)
            )}
          </div>
        </section>
      </div>

      <div className="sections">
        <section className="section">
          <h3 className="t-eyebrow">At a glance</h3>
          <div className="panel rows">
            <Stat label="Shows" value={String(shows.data.length)} />
            <Stat
              label={tour.startDate.toDate() > now ? 'Days out' : 'Days left'}
              value={String(daysRemaining(tour, now))}
              emphasised
            />
          </div>
        </section>

        {nextShow && (
          <section className="section">
            <h3 className="t-eyebrow">Next show</h3>
            <Link to={`/tours/${tour.id}/shows`} className="panel panel-pad panel-signalled stack">
              <span className="t-display-s">{nextShow.city}</span>
              <span className="t-caption truncate">{nextShow.venueName}</span>
              <div className="spread">
                <span className="t-spec-caption dim">
                  {formatDay(nextShow.date, nextShow.timezone)}
                </span>
                <span className="chip chip-accent">{countdown(nextShow.date)}</span>
              </div>
            </Link>
          </section>
        )}
      </div>
    </div>
  )
}

function ItemRow({ item, showDate }: { item: WithId<ItineraryItem>; showDate?: boolean }) {
  // Presets are seeded before times are known; the row says so rather than
  // showing a misleading midnight.
  const needsTime = item.needsTime === true

  return (
    <div className="row" style={{ padding: '11px 16px', gap: 14, alignItems: 'baseline' }}>
      <span
        className="t-timecode"
        style={{ minWidth: 54, color: needsTime ? 'var(--text-tertiary)' : undefined }}
      >
        {needsTime ? '--:--' : formatTime(item.timeUTC, item.timezone)}
      </span>
      <span className="grow truncate">{item.title}</span>
      {showDate && (
        <span className="t-spec-caption faint">{formatDay(item.timeUTC, item.timezone)}</span>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  emphasised,
}: {
  label: string
  value: string
  emphasised?: boolean
}) {
  return (
    <div className="spread" style={{ padding: '13px 16px' }}>
      <span className="t-eyebrow">{label}</span>
      <span
        className="t-display-s"
        style={{ color: emphasised ? 'var(--accent-text)' : undefined }}
      >
        {value}
      </span>
    </div>
  )
}

function startOfToday(now: Date): Date {
  const copy = new Date(now)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function daysRemaining(tour: WithId<Tour>, now: Date): number {
  const day = 86_400_000
  const today = startOfToday(now).getTime()
  const start = startOfToday(tour.startDate.toDate()).getTime()
  const end = startOfToday(tour.endDate.toDate()).getTime()
  if (today < start) return Math.round((start - today) / day)
  return Math.max(0, Math.round((end - today) / day))
}
